import { useAppDispatch, useAppSelector } from "@/lib/store";
import { useEffect, useState } from "react";
import { fetchClients, fetchClientsWithName, setName, setPage, setPageSize, setSortKey } from "./clients.slice";
import { CustomerContactFormValues } from "./clients.entity";
import { getClientById, updateContact, inviteClient } from "./clients.api";
import { Customer, CustomerSortKey } from "@/repositories/client.repository";
import { toastError, toastSuccess } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";

export default function useClients() {
  const dispatch = useAppDispatch();
  const { list, loading, error, total, filters } = useAppSelector((state) => state.clients);

  const [editingModalOpen, setEditingModalOpen] = useState(false);
  const [clientToEdit, setClientToEdit] = useState<Customer | null>(null);

  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteSaving, setInviteSaving] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const debouncedName = useDebounce(filters.name, 300);

  useEffect(() => {
    dispatch(fetchClientsWithName({ name: debouncedName }));
  }, [dispatch, filters.page, filters.pageSize, debouncedName, filters.sortKey, filters.sortOrder]);

  const setPages = (page: number) => {
    dispatch(setPage(page));
  };

  const setPagesSize = (pageSize: number) => {
    dispatch(setPageSize(pageSize));
  };

  const setNames = (name: string) => {
    dispatch(setName(name));
  };

  const setSortKeys = (sortKey: CustomerSortKey) => {
    dispatch(setSortKey(sortKey));
  };

  async function getClientContact(clientId: string) {
    try {
      setClientToEdit(await getClientById(clientId));
    } catch (err) {
      setClientToEdit(null);
      toastError(err instanceof Error ? err.message : "Failed to load the client contact");
    }
  }

  async function updateClientMetadata(id: string, contact: CustomerContactFormValues) {
    try {
      await updateContact(id, contact);
      setEditingModalOpen(false);
      toastSuccess("Client contact updated successfully");
      dispatch(fetchClients());
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Failed to update client contact");
    }
  }

  async function handleInviteClient(organizationEmail: string) {
    setInviteSaving(true);
    setInviteError(null);
    try {
      await inviteClient(organizationEmail);
      setInviteModalOpen(false);
      toastSuccess("Invitation sent successfully");
      dispatch(fetchClients());
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to invite client";
      setInviteError(errorMessage);
      toastError(errorMessage);
    } finally {
      setInviteSaving(false);
    }
  }

  return {
    list,
    loading,
    error,
    total,
    page: filters.page,
    pageSize: filters.pageSize,
    name: filters.name,
    sortKey: filters.sortKey,
    sortOrder: filters.sortOrder,
    updateClientMetadata,
    setPage: setPages,
    setPageSize: setPagesSize,
    setName: setNames,
    setSortKey: setSortKeys,
    editingModalOpen,
    setEditingModalOpen,
    getClientContact,
    clientToEdit,
    onSubmit: (vals: CustomerContactFormValues) => {
      if (!clientToEdit?.id) {
        toastError("Client not selected for update");
      } else {
        updateClientMetadata(clientToEdit?.id, vals);
      }
    },
    inviteModalOpen,
    setInviteModalOpen,
    inviteSaving,
    inviteError,
    handleInviteClient,
  };
}
